import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';

const BID_STATUS_META = {
  active:   { color: 'success', label: 'Active' },
  won:      { color: 'primary', label: 'Won' },
  lost:     { color: 'default', label: 'Lost' },
  cancelled:{ color: 'error',   label: 'Cancelled' },
};

const getBidStatusMeta = (status) =>
  BID_STATUS_META[status?.toLowerCase()] ?? { color: 'default', label: status || 'N/A' };

const AdminBids = ({
  bids,
  bidsLoading,
  bidsError,
  bidsPage,
  bidsTotalPages,
  showBidModal,
  editingBid,
  bidForm,
  bidFormError,
  bidFormLoading,
  openBidModal,
  closeBidModal,
  handleBidFormSubmit,
  handleBidFormChange,
  handleDeleteBid,
  fetchBids,
}) => {
  const handlePageChange = (_, page) => typeof fetchBids === 'function' && fetchBids(page);

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
        <CardHeader
          title="Bids"
          subheader="View and manage all bids placed across auctions"
          titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
        />
        {bidsLoading && <LinearProgress color="primary" />}
        <CardContent sx={{ pt: 0 }}>
          {bidsError && <Alert severity="error" sx={{ mb: 2 }}>{bidsError}</Alert>}

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 620 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                  <TableCell>Bidder</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!bids || bids.length === 0) && !bidsLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No bids found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (bids || []).map((bid) => {
                    const statusMeta = getBidStatusMeta(bid.status);
                    const bidderName = bid.userId?.username
                      || `${bid.userId?.profile?.firstName || ''} ${bid.userId?.profile?.lastName || ''}`.trim()
                      || 'N/A';
                    return (
                      <TableRow key={bid._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{bidderName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{bid.amount}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={statusMeta.label} color={statusMeta.color} size="small" sx={{ fontWeight: 600, borderRadius: 1.5 }} />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <IconButton size="small" color="primary" onClick={() => openBidModal(bid)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteBid(bid)}>
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {bidsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={bidsTotalPages} page={bidsPage} onChange={handlePageChange} color="primary" shape="rounded" />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Edit / Add Bid Dialog */}
      <Dialog
        open={!!showBidModal}
        onClose={closeBidModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EmojiEventsOutlinedIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>{editingBid ? 'Edit Bid' : 'Add Bid'}</Typography>
          </Stack>
          <IconButton size="small" onClick={closeBidModal}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2.5} component="form" id="bid-form" onSubmit={handleBidFormSubmit}>
            {bidFormError && <Alert severity="error">{bidFormError}</Alert>}
            <TextField
              label="Amount"
              name="amount"
              type="number"
              required
              value={bidForm?.amount ?? ''}
              onChange={handleBidFormChange}
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={closeBidModal}>Cancel</Button>
          <Button type="submit" form="bid-form" variant="contained" disabled={bidFormLoading}>
            {bidFormLoading ? 'Saving…' : editingBid ? 'Update Bid' : 'Add Bid'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminBids;
